/**
 * Modal (§13.4): short focused tasks only; labelled, focus-contained, Escape-closable, returns focus to the
 * opener. Built on Radix Dialog. Material conditions are never hidden only inside a modal (§13.1).
 */
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { cn } from '../cn';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Footer actions (Buttons). */
  actions?: ReactNode;
  /** Set when closing must be explicit (e.g. a hold confirmation); Escape still works for exit-obstruction rules. */
  closeLabel?: string;
  /** Element that opens the dialog (optional; Radix returns focus to it automatically). */
  trigger?: ReactNode;
  className?: string;
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  actions,
  closeLabel = 'Close',
  trigger,
  className,
}: ModalProps) {
  // Radix returns focus to its own Trigger; when the opener is an arbitrary control we remember it ourselves.
  const opener = useRef<Element | null>(null);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
      <Dialog.Portal>
        <Dialog.Overlay className="wd-modal__overlay" />
        <Dialog.Content
          className={cn('wd-modal__content', className)}
          onOpenAutoFocus={() => {
            opener.current = document.activeElement;
          }}
          onCloseAutoFocus={(e) => {
            if (trigger) return;
            e.preventDefault();
            const el = opener.current;
            if (el instanceof HTMLElement && el.isConnected) el.focus();
          }}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="wd-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Dialog.Title className="wd-modal__title">{title}</Dialog.Title>
            <Dialog.Close className="wd-btn wd-touch" data-variant="secondary" aria-label={closeLabel}>
              <X size={20} aria-hidden="true" />
            </Dialog.Close>
          </div>
          {description ? (
            <Dialog.Description className="wd-modal__desc">{description}</Dialog.Description>
          ) : null}
          {children}
          {actions ? <div className="wd-modal__actions">{actions}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const ModalClose = Dialog.Close;
